import { useEffect, useMemo, useRef, useState } from 'react';
import { ToastContainer } from "react-toastify";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { hideHeaderRoutes } from './constants/navigation';
import { useBeforeUnload } from './hooks/useBeforeUnload';
import { useAppUpdate } from './hooks/useAppUpdate';
import { setNavigateToLogin } from "./api/axiosInstance";
import api from "./api/axiosInstance";
import { setupDummyAuth, cleanupDummyAuth, isLocalhost, isAuthBypassEnabled } from './helpers/devMode';

import MyProfile from "./components/my-profile/MyProfile";
import PatientList from "./components/patient/PatientList";
import PatientDetail from "./components/patient/detail/PatientDetail";
import Header from "./components/header";
import ClinicalServices from "./components/patient/clinical-services/ClinicalServices";
import PatientLayout from "./components/patient/patient-layout/PatientLayout";
import "react-toastify/dist/ReactToastify.css";
import CloseButton from "./components/common/toast/close-button";
import Footer from "./components/footer";
import LoadService from "./components/patient/clinical-services/services/load-service";
import InsiderForm from "./components/insider-form";
import TestCharts from "./components/TestCharts";
import LoadInterventionForms from './components/patient/Patient-Menu/Interventions/LoadInterventionForms';
import Dashboard from "./components/dashboard/Dashboard";
import Clinicals from "./components/clinicals/Clinicals";
import ClinicalDetail from "./components/patient/clinical-details/ClinicalDetail";
import PatientMedication from "./components/patient/Patient-Menu/Medications/PatientMedication";
import PatientVaccines from "./components/patient/Patient-Menu/Vaccines/PatientVaccines";
import PatientMeasurement from "./components/patient/Patient-Menu/Measurements/PatientMeasurement";
import PatientIntervention from "./components/patient/Patient-Menu/Interventions/PatientIntervention";
import PatientMedicalSummary from "./components/patient/Patient-Menu/Medical Summary/PatientMedicalSummary";
import PatientClinicalCalc from "./components/patient/Patient-Menu/Clinical Calc/PatientClinicalCalc";
import ConfirmationModalNew from './components/common/ConfirmationModalNew';
import Alert from './components/common/Alert';
import CustomInput from './components/common/CustomInput';
import { isFieldError } from './helpers/input-validations';

import PatientChart from "./components/patient/Patient-Menu/Charting/PatientChart";
import LoadServiceForms from "./components/patient/clinical-services/services/load-service-forms";
import PortalSelection from "./components/auth/PortalSelection";
import { useClickTracker } from './hooks/useClickTracker';
import { useInactivityTimer } from './hooks/useInactivityTimer';
import ComingSoon from './components/ComingSoon';
import InactivityCheckOverlay from './components/common/InactivityCheckOverlay';
import CompliancePopup from "./components/auth/CompliancePopup";
import ResumeClinicalServiceModal from './components/common/ResumeClinicalServiceModal';
import { forgotPassword } from './redux/modules/user/actions';
import { checkSystemMaintenance } from './redux/modules/user/actions';

import { useDispatch, useSelector } from 'react-redux';

import {
  Login, ForgotPassword, CheckYourEmail, ChangeYourPassword, PasswordChangedSuccess,
  ForgotUsername, ShowYourUsername, Unauthorized, ConfirmedNewLinkSent, StoreSelectionList, ForgotUserId
} from './Auth';
import { getCurrentUserDetails, restoreAuthFromSession, handleRoleBasedNavigation, restoreUserRolesFromSession, logout, getUserRoles, restorePasswordReminder } from './redux/modules/user/actions';
import { extractTokenFromUrl, storeTokenFromUrl, shouldAcceptToken, logTokenTransfer, isValidTokenFormat, cleanupSensitiveUrlParams } from './helpers/tokenSharing';
import ConfirmedNewInviteSent from './Auth/ConfirmedNewInviteSent';
import MfaVerification from './Auth/MfaVerification';
import healthCompassLogo from '/icons/logos/health-compass.svg';
import SystemMaintenance from "./components/SystemMaintenance";
import NotFound from "./components/NotFound";
import LoadSummaryForms from './components/patient/clinical-services/medical-summary/LoadSummaryForms';
import ZendeskChatEndConfirmation from "./components/common/ZendeskChatEndConfirmation";
import ZendeskGuidedEntry from "./components/common/ZendeskGuidedEntry";
import { searchInterventions } from "./redux/modules/intervention/actions";
import {
  isActiveInterventionStatus,
  isWaitingProviderInterventionStatus,
} from "./components/patient/Patient-Menu/Interventions/data";

const RequireAuth = () => {
  console.log('RequireAuth guard executing');
  const location = useLocation();

  // Keep localhost and VITE auth bypass behavior for local development flows.
  const isViteAuthBypassEnabled = import.meta.env.VITE_AUTH_BYPASS === 'true';
  const isDevelopmentAuthBypassed = (isLocalhost() && isAuthBypassEnabled()) || isViteAuthBypassEnabled;
  const accessToken = sessionStorage.getItem('accessToken');
  const urlParams = new URLSearchParams(location.search);
  const hasCrossAppAuthParams =
    urlParams.has('token') ||
    urlParams.get('loginRequired') === 'true' ||
    urlParams.get('moveDirect') === 'true';

  // Guard executes before route component mount, so protected pages never render without a token.
  if (!isDevelopmentAuthBypassed && !accessToken) {
    // Allow root-level cross-app auth handoff URL params to be processed before redirecting.
    if (location.pathname === '/' && hasCrossAppAuthParams) {
      return <Outlet />;
    }
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  return <Outlet />;
};

const isInterventionBillingCompleted = (intervention) =>
  intervention?.interventionAssessment?.interventionAssessmentForm?.documentation
    ?.completeMedicationUpdate === true;

const getInterventionAlertState = (interventionList = []) => {
  const safeList = Array.isArray(interventionList) ? interventionList : [];

  const hasActiveInterventions = safeList.some((item) =>
    isActiveInterventionStatus(item?.status)
  );

  const hasOutstandingInterventions = safeList.some((item) => {
    const status = item?.status;
    const isActiveOrWaiting =
      isActiveInterventionStatus(status) ||
      isWaitingProviderInterventionStatus(status);
    return isActiveOrWaiting && !isInterventionBillingCompleted(item);
  });

  return { hasActiveInterventions, hasOutstandingInterventions };
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Track dismiss state (only for current session, cleared on browser close)
    const [dismissSignatureAlert, setDismissSignatureAlert] = useState(() => {
        return sessionStorage.getItem('dismissSignatureAlert') === 'true';
    });
    const passExpiry = false;
    const hideClinicalUrl = false;
    const [showCompliance, setShowCompliance] = useState(false);
    const [isLegalPageComplianceFlow, setIsLegalPageComplianceFlow] = useState(false);
    const [pendingAppUpdate, setPendingAppUpdate] = useState(false);
    const [showInactivityReturnModal, setShowInactivityReturnModal] = useState(false);

  // Get authentication state from Redux store
  const { isAuthenticated, userInfo, currentLoggedInUserDetail, userRoles, isSystemMaintenance } = useSelector((state) => state.users);

  // Pull passwordChangeReminderDays from Redux
  const { passwordChangeReminderDays } = useSelector(
    (state) => state.users
  );

  const [showpasswordchangemodel, setshowpasswordchangemodel] = useState(false);
  const [isPasswordResetFromLockedFlow, setIsPasswordResetFromLockedFlow] = useState(false);
  const [username, setUsername] = useState('');
  const [usernameerror, setusernameerror] = useState('');
  const [showInterventionAlert, setShowInterventionAlert] = useState(false);
  const [dismissInterventionAlert, setDismissInterventionAlert] = useState(false);
  const { interventions } = useSelector((state) => state.intervention);
  const { patient } = useSelector((state) => state.patients);
  // Track whether the password reminder has already been shown this session.
  // Backed by sessionStorage so it survives a page reload (e.g. after clicking
  // "Update Now" in the app update modal the value is intentionally cleared in
  // handleRefresh so the reminder still appears after an update reload).
  const [passwordReminderShownThisSession, setPasswordReminderShownThisSession] = useState(
    () => sessionStorage.getItem('passwordReminderShownThisSession') === 'true'
  );

  const markPasswordReminderShown = () => {
    sessionStorage.setItem('passwordReminderShownThisSession', 'true');
    setPasswordReminderShownThisSession(true);
  };
  
  const fieldsToCheck = [{
          field: username,
          checkFn: usernameerror,
          setErrorFn: setusernameerror,
          alter : setUsername,
          name : 'username'
  }];  



  const previousHasSignatureRef = useRef(
    Boolean(currentLoggedInUserDetail?.signature)
  );

  // Reset dismiss state only when signature transitions from present -> deleted.
  // This prevents the banner from reappearing in the same session after user closes it.
  useEffect(() => {
    const hasSignature = Boolean(currentLoggedInUserDetail?.signature);
    const hadSignature = previousHasSignatureRef.current;

    if (hadSignature && !hasSignature) {
      sessionStorage.removeItem('dismissSignatureAlert');
      setDismissSignatureAlert(false);
    }

    previousHasSignatureRef.current = hasSignature;
  }, [currentLoggedInUserDetail?.signature]);

  useEffect(() => {
    // Check if modal flag is set - show immediately when user is authenticated
    const shouldShowModal = sessionStorage.getItem('showInactivityReturnModal') === 'true';
    
    if (shouldShowModal && isAuthenticated) {
      setShowInactivityReturnModal(true);
    }
  }, [isAuthenticated, location.pathname]); // Check when pathname changes (e.g., after MFA navigation)

    // Show compliance popup first on every login when user chooses Clinical from portal selection.
    // Must show before any other popups (password expiry, continue from where you left, etc.).
    // Exception: if user signed back in within 1 hr of inactivity logout, skip compliance (timestamp in sessionStorage).
    const INACTIVITY_LOGOUT_TIMESTAMP_KEY = 'inactivityLogoutTimestamp';
    const TRIGGER_COMPLIANCE_ON_LEGAL_PAGE_KEY = 'triggerComplianceOnLegalPage';
    const FIRST_TIME_LEGAL_FLOW_KEY = 'isFirstTimeLegalFlow';
    const ONE_HOUR_MS = 60 * 60 * 1000;
    useEffect(() => {
        const shouldTriggerCompliance = sessionStorage.getItem('triggerCompliance') === 'true';
        const shouldTriggerComplianceOnLegalPage =
            sessionStorage.getItem(TRIGGER_COMPLIANCE_ON_LEGAL_PAGE_KEY) === 'true';
        const isClinicalAppRoute =
            location.pathname.startsWith('/patient') || location.pathname === '/dashboard';
        const isLegalRoute = location.pathname === '/my-profile' && location.search.includes('selected=legal');
        const inactivityLogoutTs = sessionStorage.getItem(INACTIVITY_LOGOUT_TIMESTAMP_KEY);
        const signedBackInWithinOneHour =
            inactivityLogoutTs &&
            (Date.now() - parseInt(inactivityLogoutTs, 10)) < ONE_HOUR_MS;
        const shouldShowClinicalCompliance =
            shouldTriggerCompliance &&
            !passExpiry &&
            !hideClinicalUrl &&
            isClinicalAppRoute &&
            !signedBackInWithinOneHour;
        const shouldShowLegalPageCompliance = shouldTriggerComplianceOnLegalPage && isLegalRoute;

        if (shouldShowClinicalCompliance || shouldShowLegalPageCompliance) {
            sessionStorage.removeItem('triggerCompliance');
            sessionStorage.removeItem(TRIGGER_COMPLIANCE_ON_LEGAL_PAGE_KEY);
            sessionStorage.removeItem(FIRST_TIME_LEGAL_FLOW_KEY);
            setIsLegalPageComplianceFlow(shouldShowLegalPageCompliance);
            setShowCompliance(true);
        }
    }, [location.pathname, location.search, location.key, passExpiry, hideClinicalUrl]);

    const handleComplianceAgree = async () => {
        const systemSessionId = sessionStorage.getItem('systemSessionId');
        const accessToken = sessionStorage.getItem('accessToken');
        // Only attempt the logging call when we have a live session token.
        // Without a token the request interceptor would fire navigate('/login'),
        // which races with setShowCompliance(false) and can land dual-role users
        // back on the portal selection screen before the popup even closes.
        if (systemSessionId && accessToken) {
            try {
                await api.post('/api/system-access/session/compliancy', {
                    sessionId: systemSessionId,
                    compliancyVerification: true,
                });
            } catch (error) {
                // Best-effort logging call should not block user navigation.
            }
        }

        sessionStorage.setItem('hasAcceptedCompliance', 'true');
        setShowCompliance(false);
        if (isLegalPageComplianceFlow) {
            setIsLegalPageComplianceFlow(false);
            navigate('/patients');
        }
        // If there's a pending app update, show it after compliance closes
        if (pendingAppUpdate && isUpdateAvailable) {
            setPendingAppUpdate(false);
            // Small delay to ensure compliance modal is fully closed
            setTimeout(() => {
                showUpdateNotification();
            }, 100);
        }
    };
    const handleComplianceClose = () => {
        setShowCompliance(false);
        setIsLegalPageComplianceFlow(false);
        // If there's a pending app update, show it after compliance closes
        if (pendingAppUpdate && isUpdateAvailable) {
            setPendingAppUpdate(false);
            // Small delay to ensure compliance modal is fully closed
            setTimeout(() => {
                showUpdateNotification();
            }, 100);
        }
    };

  // Determine which modal to show based on days - memoized for performance
  const { shouldShowPasswordReminder, shouldShowAccountLocked, showAnyPasswordModal } = useMemo(() => {
    const shouldShowPasswordReminder = passwordChangeReminderDays >= 60 && passwordChangeReminderDays <= 119;
    const shouldShowAccountLocked = passwordChangeReminderDays > 119;
    const showAnyPasswordModal = shouldShowPasswordReminder || shouldShowAccountLocked;

    return { shouldShowPasswordReminder, shouldShowAccountLocked, showAnyPasswordModal };
  }, [passwordChangeReminderDays]);

  // Handle modal actions
  const handleRemindLater = () => {
    markPasswordReminderShown();
    dispatch({ type: "HIDE_PASSWORD_CHANGE_REMINDER" });
  };

  const handleChangePasswordNow = (showSuccessToast = false) => {
    markPasswordReminderShown();
    dispatch({ type: "HIDE_PASSWORD_CHANGE_REMINDER" });
    dispatch(forgotPassword(currentLoggedInUserDetail.primaryEmail, navigate, showSuccessToast));
  };

  const handleOkayAccountLocked = async () => {
      markPasswordReminderShown();
      setUsername('');
      setusernameerror('');
      setIsPasswordResetFromLockedFlow(true);
      setshowpasswordchangemodel(true);
      dispatch({ type: "HIDE_PASSWORD_CHANGE_REMINDER" });
  };

  const handleCancelAccountLocked = async () => {
    try {
      markPasswordReminderShown();
      setUsername('');
      setusernameerror('');
      setIsPasswordResetFromLockedFlow(false);
      setshowpasswordchangemodel(false);
      await dispatch(logout());
      navigate('/login');
    } catch (error) {
      // Fallback navigation even if logout fails
      navigate('/login');
    }
  };

  // Set up the navigation function for axios
  useEffect(() => {
    setNavigateToLogin(navigate);
  }, [navigate]);

  // System maintenance check
  useEffect(() => {
    dispatch(checkSystemMaintenance());
  }, [location.pathname, dispatch]);

  /**
   * Set up localhost authentication bypass and handle VITE_AUTH_BYPASS
   * This effect runs once when the app starts
   */
  useEffect(() => {
    // Check for VITE_AUTH_BYPASS environment variable
    const isViteAuthBypassEnabled = import.meta.env.VITE_AUTH_BYPASS === 'true';

    if (isViteAuthBypassEnabled) {
      // If VITE_AUTH_BYPASS is enabled, remove accessToken from sessionStorage
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('userInfo');
    } else {
      // If VITE_AUTH_BYPASS is false, check for dummy token and clean it up
      const accessToken = sessionStorage.getItem('accessToken');
      if (accessToken === 'dev-dummy-token-local-development-bypass') {
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('userInfo');
        navigate('/login');
        return; // Exit early to prevent further setup
      }
    }

    // Set up dummy auth for localhost if bypass is enabled
    const wasDummyAuthSetup = setupDummyAuth();

    // Clean up dummy auth when switching away from localhost
    const handleStorageChange = () => {
      cleanupDummyAuth();
    };

    // Listen for storage changes
    window.addEventListener('storage', handleStorageChange);

    // Cleanup function
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (wasDummyAuthSetup) {
        cleanupDummyAuth();
      }
    };
  }, [navigate]);

  /**
   * Check URL parameters for isUserLoggedInToAdmin and set in sessionStorage
   * This effect runs when the location changes to detect URL parameters
   */
  useEffect(() => {
    const checkAdminLoginParameter = () => {
      // Get URL search parameters
      const urlParams = new URLSearchParams(location.search);
      const isUserLoggedInToAdmin = urlParams.get('isUserLoggedInToAdmin');

      // If the parameter exists and is 'true', set it in sessionStorage
      if (isUserLoggedInToAdmin === 'true') {
        sessionStorage.setItem('isUserLoggedInToAdmin', 'true');
      }
    };

    checkAdminLoginParameter();
  }, [location.search]);

  /**
   * Token detection and processing from URL parameters
   * This effect runs when the app starts to check for tokens passed from other applications
   */
  useEffect(() => {
    const processTokenFromUrl = async () => {
      // Check for VITE_AUTH_BYPASS environment variable
      const isViteAuthBypassEnabled = import.meta.env.VITE_AUTH_BYPASS === 'true';

      // Skip token processing if auth bypass is enabled
      if (isViteAuthBypassEnabled) {
        return;
      }

      // Extract token from URL parameters
      const tokenData = extractTokenFromUrl();

      if (tokenData.loginRequired) {
        // Admin -> Clinical handoff requires fresh login.
        // Set compliance trigger so popup appears once after successful MFA/navigation.
        sessionStorage.setItem('triggerCompliance', 'true');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('userInfo');
        navigate('/login');
        return;
      }

      if (tokenData.hasToken && tokenData.isTokenFresh && shouldAcceptToken(tokenData.sourceApp)) {
        // Simple token validation - if it was successfully decoded, it's probably valid
        if (!isValidTokenFormat(tokenData.token)) {
          logTokenTransfer('URL Token Processing - Invalid Format', tokenData.sourceApp, false);
          cleanupSensitiveUrlParams();
          return;
        }


        // Store the token and userInfo in sessionStorage
        const tokenStored = storeTokenFromUrl(tokenData.token, tokenData.userInfo);

        if (tokenStored) {
          // Log successful token transfer
          logTokenTransfer('URL Token Processing', tokenData.sourceApp, true);

          // Restore authentication state from the new token
          await dispatch(restoreAuthFromSession());

          // Load user details after restoring auth state
          const userInfoFromStorage = sessionStorage.getItem('userInfo');
          if (userInfoFromStorage) {
            try {
              const parsedUserInfo = JSON.parse(userInfoFromStorage);
              if (parsedUserInfo.userId) {
                await dispatch(getCurrentUserDetails(parsedUserInfo.userId));
                await dispatch(getUserRoles(parsedUserInfo.userId));

                navigate("/patients");
              }
            } catch (error) {
            }
          }

          // Clean up sensitive URL parameters after successful processing
          cleanupSensitiveUrlParams();
        } else {
          logTokenTransfer('URL Token Processing', tokenData.sourceApp, false);
          cleanupSensitiveUrlParams();
        }
      } else if (tokenData.hasToken && !tokenData.isTokenFresh) {
        logTokenTransfer('URL Token Processing - Expired', tokenData.sourceApp, false);
        cleanupSensitiveUrlParams();
      } else if (tokenData.hasToken && !shouldAcceptToken(tokenData.sourceApp)) {
        logTokenTransfer('URL Token Processing - Untrusted Source', tokenData.sourceApp, false);
        cleanupSensitiveUrlParams();
      }
    };

    processTokenFromUrl();
  }, []); // Run only once on app start

  /**
   * Centralized user authentication check
   * This effect runs when the app starts and whenever authentication state changes
   * It ensures user details are loaded as soon as we have authentication data
   */
  useEffect(() => {
    const checkAndLoadUserDetails = async () => {
      // Case 1: User is authenticated in store but missing user details
      if (isAuthenticated && userInfo?.userId && !currentLoggedInUserDetail) {
        await dispatch(getCurrentUserDetails(userInfo.userId));

        // Also check if user roles need to be loaded
        if (!userRoles || userRoles.length === 0) {
          await dispatch(restoreUserRolesFromSession());
        }

        return;
      }

      // Case 2: Not authenticated in store, check sessionStorage for existing session
      if (!isAuthenticated) {
        const accessToken = sessionStorage.getItem('accessToken');
        const userInfoFromStorage = sessionStorage.getItem('userInfo');

        if (accessToken && userInfoFromStorage) {
          try {
            const parsedUserInfo = JSON.parse(userInfoFromStorage);

            // Step 1: Restore authentication state from sessionStorage to Redux store
            await dispatch(restoreAuthFromSession());

            // Step 2: Load user details after restoring auth state
            if (parsedUserInfo.userId) {
              await dispatch(getCurrentUserDetails(parsedUserInfo.userId));
              // Also restore user roles from sessionStorage
              await dispatch(restoreUserRolesFromSession());
              // Restore password change reminder days so the popup shows correctly
              // after a page reload (e.g. after clicking "Update Now" in the app update modal)
              await dispatch(restorePasswordReminder());
            }
          } catch (error) {
            // Clear invalid sessionStorage data to prevent future errors
            sessionStorage.removeItem('accessToken');
            sessionStorage.removeItem('userInfo');
          }
        }
      }
    };

    checkAndLoadUserDetails();
  }, [isAuthenticated, userInfo, currentLoggedInUserDetail, userRoles, dispatch]);

  /**
   * Automatic redirection based on authentication status and user details
   * This effect handles redirecting users to appropriate screens when they have valid session
   */
  useEffect(() => {
    if (isSystemMaintenance) {
      return;
    }

    const handleAutomaticRedirection = async () => {
      // If the inactivity-return popup is pending/open, do not auto-redirect.
      // The user must choose Continue/Cancel from the popup.
      const isInactivityReturnPending =
        showInactivityReturnModal || sessionStorage.getItem('showInactivityReturnModal') === 'true';

      if (isInactivityReturnPending) {
        if (location.pathname === '/mfa-verification' || location.pathname === '/login') {
          navigate('/patients');
        }
        return;
      }

      // Check for VITE_AUTH_BYPASS environment variable
      const isViteAuthBypassEnabled = import.meta.env.VITE_AUTH_BYPASS === 'true';
      // If VITE_AUTH_BYPASS is enabled, redirect directly to /patients
      if (isViteAuthBypassEnabled && window.location.pathname === '/') {
        navigate("/patients");
        return;
      }

      // Only proceed if we're on the root path or login page and user is authenticated
      const isOnRootOrLogin = location.pathname === '/' || location.pathname === '/login';

      if (isOnRootOrLogin && isAuthenticated && currentLoggedInUserDetail && userRoles && userRoles.length > 0) {

        try {
          // Use the centralized navigation function with new userRoles from Redux store
          // This now uses the structured userRoles instead of currentLoggedInUserDetail.roles
          const includesToken = location.search.includes('token');
          if (!includesToken) {
            handleRoleBasedNavigation(
              userRoles,
              navigate,
              currentLoggedInUserDetail.acceptedPrivacyPolicy,
              currentLoggedInUserDetail.acceptedTermOfUse,
              currentLoggedInUserDetail
            );
          }
        } catch (error) {
          // Fallback to patients if navigation fails
          navigate("/patients");
        }
      }
    };

    handleAutomaticRedirection();
  }, [isAuthenticated, currentLoggedInUserDetail, userRoles, location.pathname, navigate, isSystemMaintenance, showInactivityReturnModal]);

  useBeforeUnload();
  useClickTracker();
  const { showUpdateModal, handleModalClose, handleRefresh, isUpdateAvailable, showUpdateNotification } = useAppUpdate(); // Check for app updates

  // Track if app update becomes available while compliance popup is showing
  useEffect(() => {
    if (showUpdateModal && showCompliance) {
      // App update is available but compliance is showing, mark it as pending
      setPendingAppUpdate(true);
    }
  }, [showUpdateModal, showCompliance]);

  const normalizedPathname = location.pathname.replace(/\/$/, '');
  const patientRouteMatch = normalizedPathname.match(/^\/patient\/([^/]+)/);
  const activePatientId = patient?.id || patientRouteMatch?.[1] || null;
  const interventionBannerArmedKey = `interventionBannerArmed:${userInfo?.userId || "anonymous"}:${activePatientId || "unknown"}`;
  const isClinicalAppRoute = normalizedPathname === '/patients' || normalizedPathname === '/dashboard' || normalizedPathname.startsWith('/patient/');
  const isViteAuthBypassEnabled = import.meta.env.VITE_AUTH_BYPASS === 'true';
  const isDevelopmentAuthBypassed = (isLocalhost() && isAuthBypassEnabled()) || isViteAuthBypassEnabled;
  const accessToken = sessionStorage.getItem('accessToken');
  const hasAuthSession = isDevelopmentAuthBypassed || isAuthenticated || !!accessToken;
  const isPublicAuthRoute = hideHeaderRoutes.includes(normalizedPathname);
  const shouldHideLayoutChrome =
    isPublicAuthRoute || !hasAuthSession;
  const shouldShowCompliancePopup = showCompliance && hasAuthSession && !isPublicAuthRoute;

  // Check if current route should track inactivity (not in hideHeaderRoutes)
  const shouldTrackInactivity = !shouldHideLayoutChrome;

  useEffect(() => {
    if (!activePatientId || !hasAuthSession) return;
    dispatch(searchInterventions(activePatientId));
  }, [dispatch, activePatientId, hasAuthSession]);

  useEffect(() => {
    if (!activePatientId || !hasAuthSession) {
      setShowInterventionAlert(false);
      setDismissInterventionAlert(false);
      return;
    }

    const { hasActiveInterventions, hasOutstandingInterventions } =
      getInterventionAlertState(interventions);
    const isBannerArmed =
      localStorage.getItem(interventionBannerArmedKey) === "true";

    // First sign-in/entry after Active creation only arms the banner; it becomes visible on next sign-in.
    if (hasActiveInterventions && !isBannerArmed) {
      localStorage.setItem(interventionBannerArmedKey, "true");
      setShowInterventionAlert(false);
      return;
    }

    const shouldShow =
      hasOutstandingInterventions &&
      isBannerArmed &&
      !dismissInterventionAlert;
    setShowInterventionAlert(shouldShow);

    // Reset latch once no outstanding intervention remains.
    if (!hasOutstandingInterventions) {
      localStorage.removeItem(interventionBannerArmedKey);
      setDismissInterventionAlert(false);
    }
  }, [interventions, normalizedPathname, activePatientId, hasAuthSession, interventionBannerArmedKey, dismissInterventionAlert]);

  // Inactivity timer logic
  const {
    modalOpen: inactivityModalOpen,
    setModalOpen: setInactivityModalOpen,
    countdown: inactivityCountdown,
    resetAll: resetInactivity,
    isCheckingInactivity,
  } = useInactivityTimer({
    onInactivity: () => { }, // Optionally do something when modal opens
    onCountdownEnd: () => {
      handleClose();
    },
    shouldTrackInactivity: shouldTrackInactivity,
  });

  /**
   * Control Zendesk chat button visibility based on login state and route
   * Chat button should only appear when user is logged in and not on hideHeaderRoutes
   */
  useEffect(() => {
    // Determine if chat should be visible:
    // 1. User must be logged in (currentLoggedInUserDetail exists)
    // 2. Current route must not be in hideHeaderRoutes
    const shouldShowChat = currentLoggedInUserDetail && !shouldHideLayoutChrome;
    
    // Get the chat button element from DOM
    const chatButton = document.getElementById('myChatButton');
    
    if (chatButton) {
      // Show or hide the chat button based on conditions
      chatButton.style.display = shouldShowChat ? 'flex' : 'none';
    }
  }, [currentLoggedInUserDetail, shouldHideLayoutChrome]);

  /**
   * Compliance should never remain visible on public/auth pages.
   * This prevents modal leakage when a user is redirected out of protected routes.
   */
  useEffect(() => {
    if (showCompliance && (isPublicAuthRoute || !hasAuthSession)) {
      setShowCompliance(false);
      setIsLegalPageComplianceFlow(false);
    }
  }, [showCompliance, isPublicAuthRoute, hasAuthSession]);

  /**
   * Handles user logout by dispatching logout action and navigating to login page
   * Ensures logout action completes before navigation
   */
  const handleClose = async () => {
    try {
      // Close the inactivity modal immediately
      setInactivityModalOpen(false);
      // Wait for logout action to complete before navigating
      await dispatch(logout('InactivityAutoLogout'));
      // Navigate to login page after logout is successful
      navigate('/login');
    } catch (error) {
      // Ensure modal is closed even if logout fails
      setInactivityModalOpen(false);
      // Fallback navigation even if logout fails
      navigate('/login');
    }
  }

  // Handler for Continue with Service button in inactivity return modal
  const handleContinueWithService = () => {
    const resolvedReturnUrl = sessionStorage.getItem('inactivityReturnUrl');

    sessionStorage.removeItem('showInactivityReturnModal');
    sessionStorage.removeItem('inactivityReturnUrl');
    
    setShowInactivityReturnModal(false);
    
    if (!resolvedReturnUrl) {
      navigate('/patients');
      return;
    }

    const currentUrl = window.location.pathname + window.location.search + window.location.hash;
    if (currentUrl !== resolvedReturnUrl) {
      navigate(resolvedReturnUrl);
    }
  };

  const handleStartNewFromApp = () => {
    sessionStorage.removeItem('showInactivityReturnModal');
    sessionStorage.removeItem('inactivityReturnUrl');
    setShowInactivityReturnModal(false);
    if (activePatientId) {
      navigate(`/patient/${activePatientId}/clinical-services/`);
    } else {
      navigate('/patients');
    }
  };

  // Handler for clicking the X (close icon) on the inactivity return modal
  // This should also navigate to the Patients page
  const handleInterruptedClose = () => {
    sessionStorage.removeItem('showInactivityReturnModal');
    sessionStorage.removeItem('inactivityReturnUrl');
    setShowInactivityReturnModal(false);
    navigate('/patients');
  };

  // Format timer as MM:SS
  const formatTimer = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  // Updated Modal content to match the clean design of image 2
  const inactivityContent = () => (
    <div className="text-center pt-6">
      {/* Health Compass Logo - positioned at the top */}
      <div className="flex justify-center mb-3">
        <img
          src={healthCompassLogo}
          alt="healthcompass Rx"
          className="h-16 w-auto"
        />
      </div>

      {/* Title - converted to div  */}
      <div className="text-teal-300 text-[28px] font-normal mb-4">
        Automatic Log-Out
      </div>
      <hr className="mx-[5px] border-gray-200" />
      {/* Description text  */}
      <div className="text-center text-[18px] text-teal-300 font-normal !mb-3 mt-2">
        For your security, you will be automatically logged out if there is no activity for 15 minutes.
      </div>

      {/* Timer - large and prominent */}
      <div className="text-4xl font-bold text-teal-300 font-mono tracking-wider mb-1">
        {formatTimer(inactivityCountdown)}
      </div>
    </div>
  );

  if (isSystemMaintenance) {
    return (
      <div className="min-h-screen bg-[#F5F7FA]">
        <SystemMaintenance />
      </div>
    );
  }

  return (
    <div id='main-content-area' className="flex flex-col h-screen overflow-hidden print:h-auto print:overflow-visible">
      {/* Inactivity Check Overlay - Shows while checking session on tab return */}
      <InactivityCheckOverlay isVisible={isCheckingInactivity && shouldTrackInactivity} />

      {/* Sticky Header with shadow */}
      {!shouldHideLayoutChrome && (
        <Header />
      )}

      {!shouldHideLayoutChrome && showInterventionAlert && (
        <Alert
          title="Alert"
          type="primary"
          className="m-4 mb-1 mt-1"
          handleClose={() => {
            setShowInterventionAlert(false);
            setDismissInterventionAlert(true);
          }}
          content={() => (
            <div className="flex items-center text-gray-800">
              <span>
                This patient has outstanding interventions.
                <button
                  type="button"
                  onClick={() => navigate(`/patient/${activePatientId}/interventions`)}
                  className="ml-6 font-semibold underline text-teal-300 hover:text-REELTeal"
                >
                  Click to View
                </button>
              </span>
            </div>
          )}
        />
      )}

      {/* Signature missing alert - Shows globally when user has no signature */}
      {!shouldHideLayoutChrome &&
        !currentLoggedInUserDetail?.signature &&
        !dismissSignatureAlert && (
          <Alert
            title="Alert"
            type="primary"
            className="m-4 mb-1 mt-1"
            handleClose={() => {
              setDismissSignatureAlert(true);
              sessionStorage.setItem('dismissSignatureAlert', 'true');
            }}
            content={() => (
              <div className="flex items-center text-gray-800">
                <span>
                  Add your Virtual Signature in <span className="font-semibold">My Profile</span>, then it will automatically be applied to all mandatory documentation forms.
                  <button
                    onClick={() => {
                      const currentPath = location.pathname + location.search;
                      navigate('/my-profile?selected=settings&tab=virtual-signature', {
                        state: { from: currentPath }
                      })
                    }}
                    className="ml-6 font-semibold text-teal-300 underline hover:text-REELTeal"
                  >
                    Set Up Signature.
                  </button>
                </span>
              </div>
            )}
          />
        )}
      <CompliancePopup
        open={shouldShowCompliancePopup}
        handleClose={handleComplianceClose}
        handleAgree={handleComplianceAgree}
        pharmacyName={currentLoggedInUserDetail?.pharmacyName}
      />

      {/* Toast Container */}
      <ToastContainer
        className="toaster-container"
        position="top-right"
        hideProgressBar={true}
        pauseOnHover
        closeButton={<CloseButton />}
      />

      {/* Main Content Area - Scrollable */}
      <main className="flex flex-col flex-1 min-h-0 overflow-hidden main-content print:h-auto print:overflow-visible print:min-h-0">
        {/* Routes Content - Scrollable */}
        <div className="flex-1 overflow-auto print:overflow-visible print:h-auto print:max-h-none" style={{position: 'relative'}}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/mfa-verification" element={<MfaVerification />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/forgot-username" element={<ForgotUsername />} />
            <Route path="/forgotten-user-id" element={<ForgotUserId />} />
            <Route path="/show-username" element={<ShowYourUsername />} />
            <Route path="/check-email" element={<CheckYourEmail />} />
            <Route path="/confirmed-new-link" element={<ConfirmedNewLinkSent />} />
            <Route path="/confirmed-new-invite" element={<ConfirmedNewInviteSent />} />
            <Route path="/change-password" element={<ChangeYourPassword />} />
            <Route path="/password-changed-success" element={<PasswordChangedSuccess />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/system-maintenance" element={<SystemMaintenance />} />

            <Route element={<RequireAuth />}>
              <Route index element={<div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading...</p>
                </div>
              </div>} />
              <Route path="/my-profile" element={<MyProfile />} />
              <Route path="/portal/selection" element={<PortalSelection />} />
              <Route path="/store-selection" element={<StoreSelectionList />} />
              <Route path="/test-charts" element={<TestCharts />} />

              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/clinicals" element={<Clinicals />} />
              <Route path="/patients" element={<PatientList />} />
              <Route path="/insider-form" element={<InsiderForm />} />
              <Route path="/patient/create" element={<PatientList create={true} />} />
              <Route element={<PatientLayout />}>
                <Route
                  path="/patient/:id"
                  element={<PatientDetail create={true} />}
                />
                <Route
                  path="/patient/:id/clinical-details"
                  element={<ClinicalDetail create={true} />}
                />
                <Route
                  path="/patient/:id/clinical-services"
                  element={<ClinicalServices />}
                />
                <Route
                  path="/patient/:id/education"
                  element={<ComingSoon />} />
                <Route
                  path="/patient/:id/clinical-services/:serviceId"
                  element={<LoadService />} />
                <Route
                  path="/patient/:id/clinical-services/:serviceId/form/:formType"
                  element={<LoadServiceForms />} />
                <Route
                  path="/patient/:id/medical-summary/:type/:summaryId/form/:formType"
                  element={<LoadSummaryForms />} />
                <Route
                  path="/patient/:id/interventions/:interventionId/:type/form/:formType"
                  element={<LoadInterventionForms />} />
                <Route
                  path="/patient/:id/medications"
                  element={<PatientMedication />} />
                <Route path="/patient/:id/vaccines" element={<PatientVaccines />} />
                <Route
                  path="/patient/:id/measurements"
                  element={<PatientMeasurement />} />
                <Route
                  path="/patient/:id/interventions"
                  element={<PatientIntervention />} />
                <Route
                  path="/patient/:id/interventions/:interventionId"
                  element={<PatientIntervention />} />
                <Route
                  path="/patient/:id/medical-summary/:type"
                  element={<PatientMedicalSummary />} />
                <Route
                  path="/patient/:id/medical-summary/:type/:summaryId"
                  element={<PatientMedicalSummary />} />
                <Route
                  path="/patient/:id/clinical-calc"
                  element={<PatientClinicalCalc />} />
                <Route path="/patient/:id/charting" element={<PatientChart />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </div>
      </main>

      {/* Sticky Footer */}
      {!shouldHideLayoutChrome && (
        <footer className="flex-shrink-0 bg-white border-t border-gray-200 shadow-sm">
          <Footer />
        </footer>
      )}

      <ZendeskChatEndConfirmation />
      <ZendeskGuidedEntry />

      {/* Updated Inactivity Modal - CLEAN VERSION */}
      <ConfirmationModalNew
        open={inactivityModalOpen}
        content={inactivityContent}
        handleClose={() => handleClose()}
        onCross={resetInactivity}
        confirmText="Continue"
        cancelText="Log out"
        confirmAction={resetInactivity}
        cancelBtnType="primary-outline"
        type="primary"
        hideActions={false}
        hideCloseIcon={true}
        className="!pt-0" // ✅ Remove top padding to eliminate header space
      />

      {/* ✅ Password Change Reminder Modal - Shown only after compliance popup is dismissed, once per session.
           Gated on !!currentLoggedInUserDetail so both this and Session Expired evaluate only after
           user data (including password days) is fully loaded, preventing a flicker where Session
           Expired briefly appears before the password data arrives. */}
      <ConfirmationModalNew
        open={ !!currentLoggedInUserDetail && showAnyPasswordModal && !showCompliance && !showUpdateModal && isClinicalAppRoute && !passwordReminderShownThisSession }
        title={shouldShowAccountLocked ? "Time to Update Your Password" : "Time to Update Your Password"}
        className='!p-0 !w-[630px] !min-w-[630px] !max-w-[630px]'
        content={() => (
          <div className="text-left">
            {shouldShowPasswordReminder ? (
              // Show current wording for days 46-59
              <>
                <p className="text-base font-medium text-gray-800 mb-3">
                  It&apos;s been 60 days since your last password change; please update your password to maintain account security.
                </p>
                <p className="text-sm text-gray-500">
                  Rotating your password regularity helps protect patient data and clinical system from unauthorized access.
                </p>
              </>
            ) : shouldShowAccountLocked ? (
              // Show locked account wording for days > 59
              <>
                <p className="text-base font-medium text-gray-800 mb-4">
                  Your 14-day window to update your password has expired & your account has been temporarily locked for security reasons.
                </p>
                <p className="text-sm text-gray-500">
                  To regain access, please contact your Pharmacy Manager directly or
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Email: <b>support@reeloutline.com</b>
                </p>
                <p className="text-sm text-gray-500">
                  We're here to help you get back into your account as quickly as possible.
                </p>
              </>
            ) : null}
          </div>
        )}
        handleClose={shouldShowAccountLocked ? handleCancelAccountLocked : handleChangePasswordNow}
        onCross={shouldShowAccountLocked ? handleCancelAccountLocked : handleRemindLater}
        confirmText={shouldShowAccountLocked ? "Request Password Reset" : "Remind Me Later"}
        cancelText={shouldShowAccountLocked ? "Cancel" : "Change Password Now"}
        confirmAction={shouldShowAccountLocked ? handleOkayAccountLocked : handleRemindLater}
        cancelBtnType="primary-outline"
        type="primary"
        hideActions={false}
        hideCloseIcon={false}
      />
      {/* Password Reset Request popup */}
      <ConfirmationModalNew
        open={showpasswordchangemodel && isPasswordResetFromLockedFlow}
        title="Password Reset Request"
        className='!p-0 !w-[630px] !min-w-[630px] !max-w-[630px]'
        content={() => (
          <div className="text-left">
            <div className='mb-4'>
              <CustomInput
              label="Username / Email Address:"
              name="username"
              className={`bg-white`}
              type={'text'}
              autoComplete="off"
              value={username}
              placeholder="Enter Email Address"
              onChange={(e) => {
                setUsername(e.target.value);
                if (usernameerror) {
                  setusernameerror('');
                }
              }}
              hideHistory={true}
              error={usernameerror}
              required
              />
              {usernameerror && (
                <label htmlFor="username" className="mt-1 block text-sm text-red-300">
                  {usernameerror}
                </label>
              )}
            </div>
            <div className='bg-teal-75 h-[70px] px-4 py-3 rounded-md flex items-center justify-between'>
              <p className="text-[18px] text-gray-800">
                <span className="font-semibold">Pharmacy Name:</span> {currentLoggedInUserDetail?.pharmacyName || '-'}
              </p>
            </div>  
          </div>
        )}
        onCross={handleCancelAccountLocked}
        confirmText="Send Reset Link"
        disableConfirm={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((username || '').trim())}
        cancelText="Cancel"
        handleClose={handleCancelAccountLocked}
        confirmAction={() => {
          if (username.trim() !== currentLoggedInUserDetail?.primaryEmail.trim()) {
            return setusernameerror('The email you entered does not match the logged-in user email.');
          }

          setusernameerror('');
          handleChangePasswordNow(true);
          setIsPasswordResetFromLockedFlow(false);
          setshowpasswordchangemodel(false);
        }}
      />

      {/* App Update Modal */}
      <ConfirmationModalNew
        open={ showUpdateModal && !showCompliance && isClinicalAppRoute }
        title="App Update Available"
        content={() => (
          <div className="text-center">
            <p className="text-base font-medium text-gray-800 mb-3">
               A new version of the app is ready. Refresh to update.
            </p>
            <p className="text-sm text-gray-600">
              Refreshing gives you the latest features and updates.
            </p>
          </div>
        )}
        handleClose={handleModalClose}
        onCross={() => {}}
        confirmText="Update Now"
        cancelText={null}
        confirmAction={handleRefresh}
        cancelBtnType="primary-outline"
        type="primary"
        hideActions={false}
        hideCloseIcon={true}
        verticalButtons={true}
        actionsClassName="justify-center"
      />

      {/* Clinical Service in Progress Modal - Shown only after all preceding popups are dismissed */}
      <ResumeClinicalServiceModal
        open={showInactivityReturnModal && !showCompliance && !showUpdateModal && (!showAnyPasswordModal || passwordReminderShownThisSession) && isClinicalAppRoute}
        patientId={activePatientId}
        lastAccessedBy={[currentLoggedInUserDetail?.firstName, currentLoggedInUserDetail?.lastName].filter(Boolean).join(' ') || null}
        onResume={handleContinueWithService}
        onStartNew={handleStartNewFromApp}
        onClose={handleInterruptedClose}
      />

    </div>
  );
}

export default App;
